// EnhancedTable/TileView.js
import React from 'react';
import PropTypes from 'prop-types';
import {
	Card,
	CardContent,
	CardActions,
	Typography,
	Checkbox,
	FormControlLabel
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { get } from 'lodash';

/**
 * TileView component renders a grid of cards based on row data.
 *
 * @component
 * @param {Object} props The component props.
 * @param {Array} props.rows Array of row objects to be displayed.
 * @param {Array} props.headCells Array of objects defining the header cells.
 * @param {Function} props.handleClick Callback function for card click event.
 * @param {Function} props.handleCheck Callback function for checkbox click event.
 * @param {Function} props.isSelected Function to determine if a row is selected.
 * @param {number} props.numSelected Number of selected rows.
 * @param {Function} props.onDelete Callback function to delete a row.
 * @returns {JSX.Element} A grid of cards.
 */
function TileView({ rows, headCells, handleClick, handleCheck, isSelected, numSelected, onDelete }) {
	return (
		<Grid container spacing={2} padding={2}>
			{rows.map((row, index) => {
				const selected = isSelected(row._id);
				return (
					<Grid item xs={12} sm={6} md={4} key={index}>
						<Card
							variant="outlined"
							sx={{
								borderColor: selected ? 'primary.main' : 'grey.300',
								cursor: 'pointer',
								position: 'relative'
							}}
							onClick={(event) => handleClick(event, row)}
						>
							<CardContent>
								<FormControlLabel
									control={
										<Checkbox
											color="primary"
											checked={selected}
											onClick={(event) => {
												event.stopPropagation();
												handleCheck(event, row);
											}}
										/>
									}
									label=""
									sx={{ position: 'absolute', top: 8, right: 8 }}
								/>
								{headCells.map((headCell, idx) => (
									<Typography key={idx} variant="body2">
										<strong>{headCell.label}:</strong> {get(row, headCell.id)?.toString() || ''}
									</Typography>
								))}
							</CardContent>
							<CardActions>
								{/* Add any row-specific actions here if needed */}
							</CardActions>
						</Card>
					</Grid>
				);
			})}
		</Grid>
	);
}

TileView.propTypes = {
	rows: PropTypes.array.isRequired,
	headCells: PropTypes.array.isRequired,
	handleClick: PropTypes.func.isRequired,
	handleCheck: PropTypes.func.isRequired,
	isSelected: PropTypes.func.isRequired,
	numSelected: PropTypes.number.isRequired,
	onDelete: PropTypes.func.isRequired
};

export default TileView;
